// The loading-state contract shared by apps/web-svelte (PWA) and
// apps/web-dashboard (panel): every screen that waits for data shows a
// skeleton, and both clients decide *when* through the same numbers and
// the same two pure functions below. Each client keeps its own adapter
// (a rune class in the PWA, React hooks in the panel) that owns the real
// timers and feeds them into `nextSkeletonPhase`; neither re-derives the
// rules. The vectors at the bottom are replayed by both adapters' tests,
// which is what makes "one mechanism" checkable rather than aspirational.
//
// Deliberately NOT barrel-exported from ./index.ts — import it from the
// "@bitacorapro/shared/loading" subpath so the PWA bundle doesn't pull in
// the barrel's zod/pino graph (same tradeoff as ./relative-time.ts).
/** Below this, a load resolves with no skeleton ever painted (no flicker). */
export const SKELETON_DELAY_MS = 180;
/** Once painted, a skeleton stays at least this long (no flash-off). */
export const SKELETON_MIN_VISIBLE_MS = 350;
/** A still-loading screen adds «Esto está tardando más de lo normal…» here. */
export const SKELETON_SLOW_MS = 8_000;
/** A background refresh over cached data shows «Actualizando…» after this. */
export const REVALIDATING_HINT_MS = 3_000;
/**
 * Data first, then error, then loading. Order matters: data kept from
 * an earlier success wins over a failed refresh (the screen keeps it and
 * shows an inline notice), and an error is never hidden behind a
 * skeleton. Nothing in flight and nothing to show is `disabled`, never
 * `loading` — a disabled query must not hold a skeleton forever.
 */
export function resolveLoadStatus(i) {
    if (i.hasData)
        return i.isEmpty ? "empty" : "ready";
    if (i.failed)
        return "error";
    if (i.pending)
        return "loading";
    return "disabled";
}
export const SKELETON_IDLE = { phase: "idle", shownAt: null };
/**
 * The whole delay/minimum-visible state machine. Pure: the adapter owns
 * the clock and the timer, calls this on every load start/settle and
 * timer fire, and obeys the returned command. Timer events that arrive
 * in the wrong phase (a superseded run) are ignored.
 */
export function nextSkeletonPhase(state, event, delayMs = SKELETON_DELAY_MS, minVisibleMs = SKELETON_MIN_VISIBLE_MS) {
    switch (event.type) {
        case "start":
            if (state.phase === "idle") {
                return {
                    state: { phase: "pending", shownAt: null },
                    timer: { type: "delay-elapsed", inMs: delayMs },
                };
            }
            // A new load while the skeleton is still up keeps it up; its
            // minimum is counted from the first paint, not restarted.
            if (state.phase === "holding")
                return { state: { ...state, phase: "visible" }, timer: "clear" };
            return { state, timer: "keep" };
        case "settle":
            if (state.phase === "pending")
                return { state: SKELETON_IDLE, timer: "clear" };
            if (state.phase === "visible") {
                const shown = event.at - (state.shownAt ?? event.at);
                if (shown >= minVisibleMs)
                    return { state: SKELETON_IDLE, timer: "clear" };
                return {
                    state: { ...state, phase: "holding" },
                    timer: { type: "hold-elapsed", inMs: minVisibleMs - shown },
                };
            }
            return { state, timer: "keep" };
        case "delay-elapsed":
            if (state.phase !== "pending")
                return { state, timer: "keep" };
            return { state: { phase: "visible", shownAt: event.at }, timer: "clear" };
        case "hold-elapsed":
            if (state.phase !== "holding")
                return { state, timer: "keep" };
            return { state: SKELETON_IDLE, timer: "clear" };
    }
}
/** The skeleton is painted (and its status announced) in these phases. */
export function isSkeletonVisible(phase) {
    return phase === "visible" || phase === "holding";
}
export const SKELETON_PHASE_VECTORS = [
    {
        id: "fast load paints nothing",
        actions: [
            { at: 0, do: "start" },
            { at: 100, do: "settle" },
        ],
        checks: [
            { at: 50, phase: "pending" },
            { at: 100, phase: "idle" },
            { at: 600, phase: "idle" },
        ],
    },
    {
        id: "slow load shows the skeleton after the delay",
        actions: [
            { at: 0, do: "start" },
            { at: 1000, do: "settle" },
        ],
        checks: [
            { at: 179, phase: "pending" },
            { at: 180, phase: "visible" },
            { at: 999, phase: "visible" },
            { at: 1000, phase: "idle" },
        ],
    },
    {
        id: "load just past the delay holds the minimum",
        actions: [
            { at: 0, do: "start" },
            { at: 250, do: "settle" },
        ],
        checks: [
            { at: 180, phase: "visible" },
            { at: 250, phase: "holding" },
            { at: 529, phase: "holding" },
            { at: 530, phase: "idle" },
        ],
    },
    {
        id: "a new load during the hold keeps the first paint's minimum",
        actions: [
            { at: 0, do: "start" },
            { at: 250, do: "settle" },
            { at: 300, do: "start" },
            { at: 400, do: "settle" },
        ],
        checks: [
            { at: 300, phase: "visible" },
            { at: 400, phase: "holding" },
            { at: 529, phase: "holding" },
            { at: 530, phase: "idle" },
        ],
    },
    {
        id: "settle just under the delay never paints",
        actions: [
            { at: 0, do: "start" },
            { at: 179, do: "settle" },
        ],
        checks: [
            { at: 179, phase: "idle" },
            { at: 180, phase: "idle" },
        ],
    },
    {
        // A timer due at t fires before an action at t: the delay elapses
        // first, the skeleton paints, and the settle then holds the minimum.
        id: "settle exactly at the delay paints and holds the minimum",
        actions: [
            { at: 0, do: "start" },
            { at: 180, do: "settle" },
        ],
        checks: [
            { at: 180, phase: "holding" },
            { at: 529, phase: "holding" },
            { at: 530, phase: "idle" },
        ],
    },
];
export const LOAD_STATUS_VECTORS = [
    {
        id: "first load in flight",
        inputs: { hasData: false, isEmpty: false, failed: false, pending: true },
        status: "loading",
    },
    {
        id: "loaded with rows",
        inputs: { hasData: true, isEmpty: false, failed: false, pending: false },
        status: "ready",
    },
    {
        id: "loaded, nothing to show",
        inputs: { hasData: true, isEmpty: true, failed: false, pending: false },
        status: "empty",
    },
    {
        id: "first load failed",
        inputs: { hasData: false, isEmpty: false, failed: true, pending: false },
        status: "error",
    },
    {
        id: "automatic retry after a failure keeps the error (an explicit Reintentar clears `failed` first)",
        inputs: { hasData: false, isEmpty: false, failed: true, pending: true },
        status: "error",
    },
    {
        id: "refresh failed over kept data",
        inputs: { hasData: true, isEmpty: false, failed: true, pending: false },
        status: "ready",
    },
    {
        id: "refresh in flight over cached data",
        inputs: { hasData: true, isEmpty: false, failed: false, pending: true },
        status: "ready",
    },
    {
        id: "disabled, nothing in flight",
        inputs: { hasData: false, isEmpty: false, failed: false, pending: false },
        status: "disabled",
    },
];
//# sourceMappingURL=loading.js.map